import { Immutable } from "immer";
import React from "react";

import { templates } from "@reactivated";
import type {
    ProductDescriptionBlock as ProductDescriptionBlockValue,
    ProductValuePropBlock as ProductValuePropBlockValue,
} from "@reactivated";

import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";

import { LandingProductDescriptionBlock } from "../../blocks/LandingProductDescriptionBlock";
import { LandingProductValuePropBlock } from "../../blocks/LandingProductValuePropBlock";

/**
 * toppers LandingPage ``landing_page_props`` TOP body assembly.
 *
 * FAITHFUL port of the FIRST ``{% for block in self.landing_page_props %}`` loop
 * in ``cms/landing_page.html``, which sits ABOVE the promo-bar and renders ONLY
 * two block types via ``{% if %}/{% elif %}`` (NO ``else``):
 *   - ``product_description_block`` -> the landing-page description section;
 *   - ``product_value_prop_block``  -> the landing-page value-props section.
 *
 * SILENT-DROP (load-bearing): ``landing_page_props`` is a single StreamField
 * whose union carries ~18 block types, but this TOP loop matched only the two
 * above; every other type fell through with no ``else`` and rendered nothing.
 * thelabui ``StreamField`` THROWS on an unmapped block type
 * (``React.createElement(undefined)``), so we cannot simply omit keys -- instead
 * we PRE-FILTER the value array to this island's two keys (order-preserving)
 * and pass a map covering exactly those keys. This reproduces the drop
 * AND the field-relative order. Every other ``landing_page_props`` type is
 * handled by the disjoint LandingPageBodyBottomTemplate (which runs BELOW the
 * promo-bar in the template).
 */

// The full ``landing_page_props`` union element type, and the TOP-island subset
// narrowed off it by discriminant. The narrowed element type makes the filtered
// array's StreamFieldBlockMap require EXACTLY these two keys -- thelabui's
// ``StreamField`` types ``components`` against the value's element union, and a
// plain ``.filter()`` does NOT narrow that union, so the typed predicate below
// is what lets the two-key map type-check (and the runtime throw on an unmapped
// type is what enforces the silent-drop at render time).
type LandingBlock = NonNullable<
    Immutable<{
        page: Pick<templates.LandingPageTemplate["page"], "landing_page_props">;
    }>["page"]["landing_page_props"]
>["value"][number];
type TopBlock = Extract<
    LandingBlock,
    { type: "product_description_block" | "product_value_prop_block" }
>;

const TOP_KEYS = new Set<TopBlock["type"]>([
    "product_description_block",
    "product_value_prop_block",
]);

const isTopBlock = (block: LandingBlock): block is TopBlock =>
    (TOP_KEYS as Set<string>).has(block.type);

const topComponentsMap = {
    product_description_block: (block: {
        value: Immutable<ProductDescriptionBlockValue>;
    }) => <LandingProductDescriptionBlock value={block.value} />,
    product_value_prop_block: (block: {
        value: Immutable<ProductValuePropBlockValue>;
    }) => <LandingProductValuePropBlock value={block.value} />,
} as const;

export const LandingPageBodyTop = (
    props: Immutable<{
        page: Pick<templates.LandingPageTemplate["page"], "landing_page_props">;
    }>,
): React.JSX.Element => {
    const value = (props.page.landing_page_props?.value ?? []).filter(
        isTopBlock,
    );
    return (
        <StreamField
            tagName={React.Fragment}
            value={value}
            components={topComponentsMap}
        />
    );
};
